import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token');
      return;
    }
    if (pwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pwd !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-surface">
        <div className="w-full max-w-md bg-white border border-border-default rounded p-8 text-center space-y-4">
          <WarningCircle size={40} weight="duotone" className="text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-text-primary">Invalid reset link</h2>
          <p className="text-text-secondary">This link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password">
            <Button variant="outline" className="w-full">Request new link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-surface">
      <div className="w-full max-w-md space-y-6">
        {done ? (
          <div className="bg-white border border-border-default rounded p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50">
              <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">Password reset!</h2>
            <p className="text-text-secondary">Redirecting you to the login page…</p>
          </div>
        ) : (
          <div className="bg-white border border-border-default rounded p-8 space-y-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-text-primary">
                Set a new password
              </h2>
              <p className="mt-2 text-text-secondary">Choose a strong password (min. 6 characters).</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rp-pwd">New Password</Label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                  <Input
                    id="rp-pwd"
                    type="password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    className="pl-10 h-11"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-confirm">Confirm New Password</Label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="pl-10 h-11"
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-accent-brand hover:bg-accent-brand-hover text-white"
                data-testid="reset-submit-button"
              >
                {loading ? 'Resetting…' : 'Reset password'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
